"use client";

import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import "swiper/css";
import ProjectCard from "../card/projectCard";

// SG variant of the past-project block. Same continuous free-scrolling drift
// motion as v1's cardStack.tsx (speed:5000/delay:0 for a smooth constant
// scroll, tripled array + loopAdditionalSlides for a seamless loop,
// touch-speed override, same interrupt-safe nav-button logic), but fixed to
// exactly 2 cards visible at a time (slidesPerView: 2, fluid ProjectCard)
// instead of v1's auto-width single-card-focus sizing, with prev/next arrows.
export default function CaseStudiesGrid({ data }: { data: any }) {
    const swiperRef = useRef<SwiperType | null>(null);

    const navigate = (dir: "prev" | "next") => {
        const swiper = swiperRef.current;
        if (!swiper) return;
        swiper.autoplay.stop();
        const go = () =>
            dir === "next" ? swiper.slideNext(300) : swiper.slidePrev(300);
        if (swiper.animating) {
            swiper.animating = false;
            swiper.slideTo(swiper.activeIndex, 0, false);
            requestAnimationFrame(go);
        } else {
            go();
        }
    };

    return (
        <section className="bg-gradientPurple px-4 md:px-8 pb-[12vw] md:pb-[6vw]">
            <div className="bg-rainbow rounded-[1.5rem] md:rounded-[2.5rem] px-6 md:px-12 py-12 md:py-16">
                <h2 className="text-center text-darkGrey font-bold text-2xl md:text-5xl">
                    {data.heading}
                </h2>

                <div className="flex justify-center mt-6">
                    <a
                        href="/works"
                        className="inline-flex items-center bg-purple text-white font-medium text-sm md:text-base px-6 py-3 rounded-full hover:bg-[#00fe8a] hover:text-purple transition-colors duration-300"
                    >
                        Explore All Case Studies
                    </a>
                </div>

                <div
                    className="relative mt-10 md:mt-12"
                    onMouseEnter={() => swiperRef.current?.autoplay?.stop()}
                    onMouseLeave={() => swiperRef.current?.autoplay?.start()}
                >
                    <button
                        onClick={() => navigate("prev")}
                        className="absolute -left-2 md:-left-5 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-purple/30 bg-white text-purple shadow-md transition-all duration-300 hover:border-purple hover:bg-purple hover:text-white md:h-12 md:w-12"
                        aria-label="Previous case studies"
                    >
                        <ChevronLeftIcon className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => navigate("next")}
                        className="absolute -right-2 md:-right-5 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-purple/30 bg-white text-purple shadow-md transition-all duration-300 hover:border-purple hover:bg-purple hover:text-white md:h-12 md:w-12"
                        aria-label="Next case studies"
                    >
                        <ChevronRightIcon className="h-5 w-5" />
                    </button>

                    <Swiper
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper;
                        }}
                        modules={[Autoplay]}
                        slidesPerView={1}
                        spaceBetween={24}
                        breakpoints={{
                            1024: {
                                slidesPerView: 2,
                                spaceBetween: 32,
                            },
                        }}
                        loop={true}
                        loopAdditionalSlides={data.projects?.length}
                        speed={5000}
                        autoplay={{
                            delay: 0,
                            disableOnInteraction: false,
                            stopOnLastSlide: false,
                            waitForTransition: true,
                            pauseOnMouseEnter: true,
                        }}
                        allowTouchMove={true}
                        onTouchStart={(swiper) => {
                            swiper.params.speed = 300;
                        }}
                        onTransitionEnd={(swiper) => {
                            swiper.params.speed = 5000;
                        }}
                    >
                        {[
                            ...(data.projects || []),
                            ...(data.projects || []),
                            ...(data.projects || []),
                        ].map((project: any, index: number) => (
                            <SwiperSlide
                                key={index}
                                className="!h-auto !bg-transparent !text-left"
                            >
                                <ProjectCard
                                    data={project}
                                    index={index}
                                    fluid
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
            </div>
        </section>
    );
}
